import { Component, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';
import { EDocumentType } from '@common/core/models';
import { Document } from '@common/core/models/';
import { DocumentsService, LoaderService } from '@common/core/services';
import { interpolateDocumentHtml } from '@web/core/utils/document-pdf-generator.util';
import { finalize, map } from 'rxjs';

export type TPolicyType = EDocumentType | 'cookie' | 'privacy';

const IFRAME_URLS: Partial<Record<TPolicyType, string>> = {
  cookie: 'https://app.legalblink.it/api/documents/6712554ff92bf0001ae875a8/cookie-policy-it',
  privacy:
    'https://app.legalblink.it/api/documents/6712554ff92bf0001ae875a8/privacy-policy-per-siti-web-o-e-commerce-it',
};

export const POLICY_TITLES: Partial<Record<TPolicyType, string>> = {
  cookie: bitfToTranslate('POLICY.COOKIE_TITLE'),
  privacy: bitfToTranslate('POLICY.PRIVACY_TITLE'),
  [EDocumentType.TERMS_AND_CONDITIONS_CASADOC]: bitfToTranslate('POLICY.TERMS_TITLE'),
  [EDocumentType.TERMS_AND_CONDITIONS_PAID_SERVICES]: bitfToTranslate('POLICY.TERMS_PAID_SERVICES_TITLE'),
  [EDocumentType.TECHNICAL_REPORT_CONTRACT]: bitfToTranslate('POLICY.TECHNICAL_REPORT_CONTRACT_TITLE'),
  [EDocumentType.APE_CONTRACT]: bitfToTranslate('POLICY.APE_CONTRACT_TITLE'),
  [EDocumentType.REQUEST_FOR_DEED_OF_ORIGIN_CONTRACT]: bitfToTranslate('POLICY.DEED_ORIGIN_CONTRACT_TITLE'),
  [EDocumentType.BOOKLET_DELEGATION]: bitfToTranslate('POLICY.BOOKLET_DELEGATION_TITLE'),
  [EDocumentType.COLLABORATOR_CONTRACT]: bitfToTranslate('POLICY.COLLABORATOR_CONTRACT_TITLE'),
  [EDocumentType.SURVEYOR_CONTRACT]: bitfToTranslate('POLICY.SURVEYOR_CONTRACT_TITLE'),
  [EDocumentType.PARTNERSHIP_CONTRACT]: bitfToTranslate('POLICY.PARTNER_TITLE'),
  [EDocumentType.APE_SURVEY]: bitfToTranslate('POLICY.APE_SURVEY_TITLE'),
  [EDocumentType.EXTRA_TEMPLATE]: bitfToTranslate('POLICY.EXTRA_TEMPLATE_TITLE'),
  [EDocumentType.COST_SUMMARY_TEMPLATE]: bitfToTranslate('POLICY.COST_SUMMARY_TEMPLATE_TITLE'),
};

@Component({
  template: '',
})
export abstract class SuperPolicyComponent {
  protected loaderService = inject(LoaderService);
  protected documentsService = inject(DocumentsService);
  protected sanitizer = inject(DomSanitizer);

  policyHtml = signal<string>('');
  iframeUrl = signal<SafeResourceUrl | null>(null);

  getTitle(policyType: TPolicyType): string {
    return POLICY_TITLES[policyType] || '';
  }

  parseParamToPolicyType(policy: string): TPolicyType | undefined {
    if (policy in IFRAME_URLS) {
      return policy as TPolicyType;
    }
    const policyAsEnum = policy.toUpperCase().replace(/-/g, '_');

    if (policyAsEnum in EDocumentType) {
      return policyAsEnum as TPolicyType;
    }

    return undefined;
  }

  loadPolicy(policyType: TPolicyType, interpolationMap?: Record<string, string>): void {
    const iframeUrl = IFRAME_URLS[policyType];
    if (iframeUrl) {
      this.iframeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(iframeUrl));
    } else {
      this.loadDocument(policyType, interpolationMap);
    }
  }

  private loadDocument(policyType: TPolicyType, interpolationMap?: Record<string, string>) {
    this.loaderService.show();
    this.documentsService
      .get<Document>({
        filter: { type: policyType as EDocumentType },
        populate: '*',
      })
      .pipe(
        map((response) => response.content),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(async (documents) => {
        const strapiDocument = documents?.[0];
        if (strapiDocument.html) {
          const html = interpolationMap
            ? interpolateDocumentHtml({ html: strapiDocument.html, interpolationMap })
            : strapiDocument.html;
          this.policyHtml.set(html);
        }
      });
  }
}
